import { motion } from "framer-motion";
import { Smile, Trophy, TrendingDown, Users, GraduationCap, Briefcase } from "lucide-react";
import Seo from "@/lib/Seo";
import PageHero from "@/components/common/PageHero";
import { easeOut } from "@/components/common/Reveal";
import { RESULTATS } from "@/data/site";

const ICONS = { Smile, Trophy, TrendingDown, Users, GraduationCap, Briefcase };

export default function Resultats() {
  return (
    <>
      <Seo
        title="Résultats & indicateurs de nos formations"
        description="Taux de réussite, de satisfaction, d'insertion et nombre de stagiaires formés par EDUCASER GROUP. Indicateurs de résultats de nos formations transport."
        path="/resultats"
      />
      <PageHero
        breadcrumb={[{ label: "Accueil", to: "/" }, { label: "Résultats" }]}
        eyebrow="Nos indicateurs"
        title="Les résultats de nos formations"
        intro={`Indicateurs de résultats ${RESULTATS.annee}. Mise à jour : ${RESULTATS.maj}.`}
      />

      <div className="bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          {/* Global KPIs */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {RESULTATS.global.map((k, i) => {
              const Icon = ICONS[k.icon] || Trophy;
              return (
                <motion.div
                  key={k.label}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, ease: easeOut, delay: (i % 3) * 0.08 }}
                  className="rounded-2xl border border-slate-200 bg-white p-8 transition-shadow hover:shadow-[0_16px_48px_rgba(0,35,102,0.1)]"
                  data-testid={`resultat-${i}`}
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-red/10 text-brand-red">
                    <Icon className="h-6 w-6" />
                  </div>
                  <p className="mt-5 font-display text-5xl font-black tracking-tight text-brand-blue">{k.value}</p>
                  <p className="mt-2 text-sm font-medium text-slate-500">{k.label}</p>
                </motion.div>
              );
            })}
          </div>

          {/* Per formation table */}
          <div className="mt-16 overflow-hidden rounded-3xl border border-slate-200">
            <div className="bg-brand-blue px-6 py-5">
              <h2 className="font-display text-xl font-bold text-white">Détail par formation ({RESULTATS.annee})</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px] text-left text-sm" data-testid="resultats-table">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>
                    <th className="px-6 py-4 font-semibold">Formation</th>
                    <th className="px-6 py-4 font-semibold">Taux de réussite</th>
                    <th className="px-6 py-4 font-semibold">Satisfaction</th>
                    <th className="px-6 py-4 font-semibold">Stagiaires formés</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-brand-blue">
                  {RESULTATS.parFormation.map((r) => (
                    <tr key={r.formation} className="transition-colors hover:bg-slate-50">
                      <td className="px-6 py-4 font-semibold">{r.formation}</td>
                      <td className="px-6 py-4 font-bold text-brand-red">{r.reussite}</td>
                      <td className="px-6 py-4">{r.satisfaction}</td>
                      <td className="px-6 py-4">{r.stagiaires}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <p className="mt-8 text-sm leading-relaxed text-slate-400">
            Les indicateurs sont calculés à partir des sessions réalisées et des enquêtes de
            satisfaction menées auprès des stagiaires. Ils sont actualisés chaque année. Certaines
            formations récentes ou à faible effectif ne disposent pas encore de données statistiquement
            significatives.
          </p>
        </div>
      </div>
    </>
  );
}
